import { Button, Popover, SearchInput, lucideIcons, Separator } from '@round/creatorbase-ui';
import { useProjectCampaignGroups, ProjectCampaignOption } from './useProjectCampaignGroups';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { ReactNode, useCallback, useState } from 'react';
import { ExplorePlatform } from '../CreatorExploreTable/helpers';
import styles from './AddCreatorToCampaignsDropdown.module.css';
import ProjectCampaignsList from './ProjectCampaignsList/ProjectCampaignsList';
import CampaignItem from './CampaignItem/CampaignItem';
import { FormikProvider } from 'formik';
import CreatePostForm, { useCreatePostForm } from './CreatePostForm/CreatePostForm';
import { Rate } from './CreatePostForm/helpers';
import { creatorbase } from '@round/api';

const { PlusIcon } = lucideIcons;

const SEARCH_DEBOUNCE_MS = 500;

type Props = {
    accountId: number | undefined;
    /**
     * Answers with the account when the creator has none yet, and is awaited on save before the post
     * is created. Rejecting it abandons the save, so whatever fetches the account reports its own
     * failure.
     */
    resolveAccountId?: () => Promise<number | undefined>;
    clientInfluencer: creatorbase.ClientInfluencer | null | undefined;
    /** Overrides the creator's saved rate — used where a rate belongs to the flow, not the creator's account. */
    rate?: Rate | null;
    platform: ExplorePlatform | undefined;
    /**
     * Replaces the built-in trigger button. Pass a `Popover.Anchor` when what opens the picker sits
     * elsewhere — a menu item, say — so the content still has something to position against.
     */
    trigger?: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    onPostCreated?: (post: creatorbase.Post) => void;
};

const AddCreatorToCampaignsDropdown = ({
    accountId,
    resolveAccountId,
    clientInfluencer,
    rate,
    platform,
    trigger,
    open: controlledOpen,
    onOpenChange,
    onPostCreated,
}: Props) => {
    const { selectedClient } = useClientSelect();

    const [selectedCampaign, setSelectedCampaign] = useState<ProjectCampaignOption | null>(null);
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const open = controlledOpen ?? uncontrolledOpen;
    const [search, setSearch] = useState('');

    const queryEnabled = Boolean(open && selectedClient?.id && platform);
    const { groups, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useProjectCampaignGroups(
        {
            client_id: selectedClient?.id,
            search,
            platform,
            managed_by: 'creatorbase',
        },
        { enabled: queryEnabled }
    );

    const setOpen = useCallback(
        (next: boolean) => {
            setUncontrolledOpen(next);
            onOpenChange?.(next);
        },
        [onOpenChange]
    );

    const createPostForm = useCreatePostForm(
        { accountId, resolveAccountId, campaign: selectedCampaign, clientInfluencer, rate },
        {
            onSuccess: (post) => {
                setOpen(false);
                setSearch('');
                setSelectedCampaign(null);
                onPostCreated?.(post);
            },
        }
    );

    // A save in flight owns the picker until it settles: the campaign it was started against is the
    // one the post is created for, so nothing may clear it out from under the request.
    const isSubmitting = createPostForm.isSubmitting;

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && isSubmitting) {
                return;
            }

            setOpen(next);
            if (!next) {
                setSearch('');
                setSelectedCampaign(null);
                createPostForm.resetForm();
            }
        },
        [createPostForm, isSubmitting, setOpen]
    );

    // An account that is still to be created is one the save can make, so the picker opens on the
    // promise of one as readily as on an account already there.
    const isTriggerDisabled = (!accountId && !resolveAccountId) || !platform;
    const selectedCampaignImage = groups.find((group) =>
        group.options.some((campaign) => campaign.id === selectedCampaign?.id)
    )?.image;

    return (
        <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
            {trigger ?? (
                <Popover.Trigger asChild>
                    <Button size="xs" disabled={isTriggerDisabled}>
                        <PlusIcon /> Add to Campaigns
                    </Button>
                </Popover.Trigger>
            )}

            <Popover.Content align="end" className={styles.dropdown}>
                <SearchInput
                    placeholder="Search projects..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setSelectedCampaign(null);
                    }}
                    debounce={SEARCH_DEBOUNCE_MS}
                    disabled={!queryEnabled || isSubmitting}
                    aria-label="Search projects"
                />

                {!selectedCampaign && (
                    <ProjectCampaignsList
                        groups={groups}
                        isLoading={isLoading}
                        hasError={Boolean(error)}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        onEndReached={() => {
                            if (hasNextPage && !isFetchingNextPage) {
                                fetchNextPage();
                            }
                        }}
                        onSelect={setSelectedCampaign}
                        selectedCampaign={selectedCampaign}
                    />
                )}

                {selectedCampaign && (
                    <>
                        <CampaignItem
                            isClearable={!isSubmitting}
                            campaign={selectedCampaign}
                            image={selectedCampaignImage}
                            onClear={() => setSelectedCampaign(null)}
                        />

                        <FormikProvider value={createPostForm}>
                            <CreatePostForm
                                campaign={selectedCampaign}
                                clientInfluencer={clientInfluencer}
                                rate={rate}
                            />
                        </FormikProvider>
                    </>
                )}

                <Separator />

                <div className={styles.actions}>
                    <Button
                        variant="default"
                        size="xs"
                        disabled={!selectedCampaign || isSubmitting}
                        onClick={createPostForm.submitForm}
                        isLoading={isSubmitting}
                    >
                        Save
                    </Button>
                    <Button variant="ghost" size="xs" disabled={isSubmitting} onClick={() => handleOpenChange(false)}>
                        Cancel
                    </Button>
                </div>
            </Popover.Content>
        </Popover>
    );
};

export default AddCreatorToCampaignsDropdown;
